import { cn } from '@/utils';
import { CheckIcon } from '@heroicons/react/24/solid';

export interface Step {
  id: string;
  title: string;
  description?: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  onStepClick?: (stepIndex: number) => void;
}

export function Stepper({
  steps,
  currentStep,
  orientation = 'horizontal',
  className,
  onStepClick,
}: StepperProps) {
  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'complete';
    if (index === currentStep) return 'current';
    return 'upcoming';
  };

  if (orientation === 'vertical') {
    return (
      <nav className={cn('flex flex-col', className)}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps.length - 1;

          return (
            <div key={step.id} className="relative flex gap-4">
              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn(
                    'absolute left-4 top-10 w-0.5 h-full -ml-px',
                    status === 'complete'
                      ? 'bg-primary-600 dark:bg-primary-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                  )}
                />
              )}

              {/* Step indicator */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => onStepClick?.(index)}
                  disabled={!onStepClick || status === 'upcoming'}
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                    status === 'complete' &&
                      'bg-primary-600 text-white dark:bg-primary-500',
                    status === 'current' &&
                      'bg-primary-600 text-white dark:bg-primary-500 ring-4 ring-primary-100 dark:ring-primary-900/30',
                    status === 'upcoming' &&
                      'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                    onStepClick && status !== 'upcoming' && 'cursor-pointer hover:opacity-80'
                  )}
                >
                  {status === 'complete' ? (
                    <CheckIcon className="h-5 w-5" />
                  ) : (
                    index + 1
                  )}
                </button>
              </div>

              {/* Step content */}
              <div className="pb-8">
                <p
                  className={cn(
                    'text-sm font-medium',
                    status === 'current'
                      ? 'text-primary-600 dark:text-primary-400'
                      : status === 'complete'
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {step.title}
                </p>
                {step.description && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </nav>
    );
  }

  // Horizontal orientation
  return (
    <nav className={cn('flex items-center justify-between', className)}>
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              {/* Step indicator */}
              <button
                onClick={() => onStepClick?.(index)}
                disabled={!onStepClick || status === 'upcoming'}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
                  status === 'complete' &&
                    'bg-primary-600 text-white dark:bg-primary-500',
                  status === 'current' &&
                    'bg-primary-600 text-white dark:bg-primary-500 ring-4 ring-primary-100 dark:ring-primary-900/30',
                  status === 'upcoming' &&
                    'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                  onStepClick && status !== 'upcoming' && 'cursor-pointer hover:opacity-80'
                )}
              >
                {status === 'complete' ? (
                  <CheckIcon className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </button>

              {/* Step label */}
              <div className="mt-2 text-center">
                <p
                  className={cn(
                    'text-sm font-medium',
                    status === 'current'
                      ? 'text-primary-600 dark:text-primary-400'
                      : status === 'complete'
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  )}
                >
                  {step.title}
                </p>
              </div>
            </div>

            {/* Connector */}
            {!isLast && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-4',
                  status === 'complete'
                    ? 'bg-primary-600 dark:bg-primary-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

interface StepContentProps {
  children: React.ReactNode;
  className?: string;
}

export function StepContent({ children, className }: StepContentProps) {
  return (
    <div className={cn('mt-8', className)}>
      {children}
    </div>
  );
}

interface StepActionsProps {
  children: React.ReactNode;
  className?: string;
}

export function StepActions({ children, className }: StepActionsProps) {
  return (
    <div className={cn('mt-8 flex items-center justify-between', className)}>
      {children}
    </div>
  );
}
